import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { BasicScrollView } from "@/components/BasicScrollView";
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import { CustomDivider } from "@/components/CustomDivider";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  alignLeft: {
    alignSelf: 'flex-start',
  },
  checkboxWithIcon: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

export default function ProfileSettings() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const user_info = useSelector((state: RootState) => state.user.user_info);
  const { edit } = useLocalSearchParams<{ edit?: string }>();
  const [publicLink, setPublicLink] = useState(AppUtils.getPublicProfileLinkFragment(communityMembership));

  const [publicProfile, setPublicProfile] = useState(!communityMembership.hidepublicprofile);
  const [publicAge, setPublicAge] = useState(user_info.publicage ?? false);

  const onSubmit = useCallback(async() => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "ProfileSettings.onSubmit",
        "/rabbl/api/set_profile_settings",
        "POST",
        {
          domain: domain,
          publicprofile: publicProfile,
          publicage: publicAge,
          publiclink: publicLink,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));

      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "ProfileSettings.onSubmit");
    } finally {
      hideSpinner();
    }
  },
  [
    domain,
    publicProfile,
    publicAge,
    publicLink,
  ]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Profile Settings"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <Checkbox
            status={publicProfile ? 'checked' : 'unchecked'}
            onPress={() => setPublicProfile(publicProfile ? false : true)}
            label="Enable public profile"
          />

          <Checkbox
            status={publicAge ? 'checked' : 'unchecked'}
            onPress={() => setPublicAge(publicAge ? false : true)}
            label="Show age"
            disabled={!publicProfile}
          />

          <CustomDivider fullWidth={true} />

          <AppText disabled={!publicProfile}>Public profile link</AppText>

          <TextInput
            value={publicLink}
            onChangeText={(x) => { setPublicLink(x.replaceAll(/[^a-zA-Z0-9\-]/gi, "").toLowerCase()); }}
            placeholder="Public profile link"
            style={styles.formElement}
            autoCapitalize='none'
            onSubmitEditing={onSubmit}
            disabled={!publicProfile}
          />

          <CustomDivider fullWidth={true} />

          <View style={styles.buttonWrapper}>
            <Button95 onPress={onSubmit}>{ edit ? "Save" : "Next" }</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />

    </BasicScrollView>
  );
}
